import React from "react";
import { motion } from "framer-motion";
import { Flourish, WheatStalk } from "./brand/Ornaments";

const About = () => {
  return (
    <section
      id="sobre"
      className="relative py-24 sm:py-32 overflow-hidden bg-[var(--sn-bg)]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Image collage */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="relative"
          >
            <div className="absolute -top-6 -left-6 text-[var(--sn-gold)] opacity-30 hidden md:block">
              <WheatStalk className="w-14 h-28" />
            </div>

            <div className="grid grid-cols-5 grid-rows-6 gap-3 aspect-[5/6]">
              <div className="col-span-3 row-span-4 overflow-hidden sn-frame">
                <img
                  src="https://images.unsplash.com/photo-1447279506476-3faec8071eee?crop=entropy&cs=srgb&fm=jpg&w=1100&q=85"
                  alt="Mãos amassando a massa fresca"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="col-span-2 row-span-3 col-start-4 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1551183053-bf91a1d81141?crop=entropy&cs=srgb&fm=jpg&w=800&q=85"
                  alt="Preparo artesanal das massas"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="col-span-2 row-span-3 col-start-4 row-start-4 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1546527276-0d0a6988a1aa?crop=entropy&cs=srgb&fm=jpg&w=800&q=85"
                  alt="Ingredientes italianos frescos"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="col-span-3 row-span-2 row-start-5 overflow-hidden sn-frame">
                <img
                  src="https://images.unsplash.com/photo-1579349443343-73da56a71a20?crop=entropy&cs=srgb&fm=jpg&w=1100&q=85"
                  alt="Nhoque artesanal pronto"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="absolute -bottom-8 -right-2 sm:-right-6 bg-[var(--sn-bg-surface)] border border-[var(--sn-border-strong)] px-6 py-5 max-w-[210px] sn-frame">
              <p className="font-display text-4xl gold-text leading-none">
                +9
              </p>
              <p className="text-[var(--sn-cream-soft)]/85 text-xs mt-2 tracking-wide">
                anos servindo tradição italiana
              </p>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[var(--sn-gold)]" />
              <span className="text-[var(--sn-gold)] tracking-[0.3em] text-xs uppercase">
                Sobre a Casa
              </span>
            </div>

            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--sn-cream)] leading-[1.05] mb-8"
              data-testid="about-headline"
            >
              Uma cozinha de família.{" "}
              <span className="italic gold-text">Uma receita de geração.</span>
            </h2>

            <div className="space-y-5 text-[var(--sn-cream-soft)]/90 text-base sm:text-lg leading-relaxed font-body">
              <p>
                A <span className="text-[var(--sn-gold)]">Santo Nhoque</span>{" "}
                nasceu em 2015 do desejo de levar para a sua casa o sabor da
                Itália que conhecemos desde a infância — aquele que vinha das
                mãos das nossas nonnas, no domingo, com o aroma do molho
                fervendo ao fundo.
              </p>
              <p>
                Cada massa é feita à mão, em pequenas porções, com batata
                fresca, farinha selecionada e o tempo necessário para que o
                ponto fique exatamente como deve ser. Nada de pressa. Nada de
                indústria. Apenas tradição, técnica e carinho.
              </p>
              <p className="font-serif-alt italic text-[var(--sn-gold)] text-xl">
                &ldquo;Comer massa é abraçar a Itália de mãos dadas com quem
                você ama.&rdquo;
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-6 border-t border-[var(--sn-border)] pt-8">
              <div data-testid="about-stat-1">
                <p className="font-display text-3xl gold-text">100%</p>
                <p className="text-[var(--sn-cream-soft)]/80 text-sm mt-1">
                  Receita artesanal
                </p>
              </div>
              <div data-testid="about-stat-2">
                <p className="font-display text-3xl gold-text">2015</p>
                <p className="text-[var(--sn-cream-soft)]/80 text-sm mt-1">
                  Ano de fundação
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-24 flex justify-center text-[var(--sn-gold)]/70">
          <Flourish className="w-40 h-5" />
        </div>
      </div>
    </section>
  );
};

export default About;
